const ANALYTICS_URL = "https://analytics.screenify.fun/api";

// Кэш для IP адреса
let cachedIP: string | null = null;

// Импортируем функцию для определения региона
import { getRegionByDomain } from "./static-res";
// Импортируем трекер времени просмотра
import { getGlobalWatchTimeTracker } from "./watch-time-tracker";

// Функция для генерации правильного visitorId
export function generateVisitorId(userId?: string): string {
  if (userId && userId !== "unknown") {
    return "msx-" + userId;
  }
  return "msx-" + Math.random().toString(36).slice(2);
}

async function getUserIP(): Promise<string> {
  // Проверяем кэш
  if (cachedIP) {
    return cachedIP;
  }
  
  // Проверяем sessionStorage
  const storedIP = sessionStorage.getItem("screenify:userIP");
  if (storedIP) {
    cachedIP = storedIP;
    return storedIP;
  }
  
  try {
    // Пробуем получить IP через API Screenify
    const response = await fetch("https://api.screenify.shop/api/user-info/ip");
    const data = await response.json();
    if (data.ip) {
      cachedIP = data.ip;
      sessionStorage.setItem("screenify:userIP", data.ip);
      return data.ip;
    }
  } catch (error) {
    console.warn("[Analytics] Failed to get IP from Screenify API:", error);
  }
  
  try {
    // Fallback: используем внешний сервис для получения IP
    const response = await fetch("https://api.ipify.org?format=json");
    const data = await response.json();
    if (data.ip) {
      cachedIP = data.ip;
      sessionStorage.setItem("screenify:userIP", data.ip);
      return data.ip;
    }
  } catch (error) {
    console.warn("[Analytics] Failed to get IP from external service:", error);
  }
  
  return "unknown";
}

function getDeviceType(): string {
  if (typeof navigator !== "undefined") {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.includes("mobi")) return "mobile";
    if (ua.includes("tablet")) return "tablet";
    if (ua.includes("tv")) return "smarttv";
    if (ua.includes("mac") || ua.includes("windows") || ua.includes("linux")) return "desktop";
  }
  return "unknown";
}

function getDomain(): string {
  if (typeof location !== "undefined") return location.hostname;
  return "unknown";
}

function getCurrentTime(): string {
  return new Date().toISOString();
}

export async function sendPlayerAnalytics({
  type,
  title,
  imdbid,
  userid = "unknown",
  audiolang = "unknown",
  subslang = "unknown",
  watchtime = "0",
  domain = getDomain(),
  userRegion = getRegionByDomain(),
  userCountry = "unknown",
  useBeacon = false,
}: {
  type: string;
  title: string;
  imdbid: string;
  userid?: string;
  audiolang?: string;
  subslang?: string;
  watchtime?: string;
  domain?: string;
  userRegion?: string;
  userCountry?: string;
  useBeacon?: boolean;
}): Promise<void> {
  // Получаем реальное время просмотра из трекера, если не передано явно
  let actualWatchTime = watchtime;
  if (watchtime === "0" || watchtime === "unknown") {
    const tracker = getGlobalWatchTimeTracker();
    if (tracker && tracker.getWatchTime() > 0) {
      actualWatchTime = tracker.getWatchTimeInMinutes();
    }
  }

  const data = {
    type,
    title,
    episodeTitle: "unknown",
    imdbid,
    userid,
    visitorId: generateVisitorId(userid),
    audiolang,
    subslang,
    domain,
    watchtime: actualWatchTime,
    date: getCurrentTime(),
    views: 1,
    userRegion,
    userCountry,
  };
  const formData = new FormData();
  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      formData.append(key, String((data as any)[key]));
    }
  }

  try {
    if (useBeacon && navigator.sendBeacon) {
      // Используем sendBeacon для надежной отправки при закрытии страницы
      const success = navigator.sendBeacon(`${ANALYTICS_URL}/statistics`, formData);
      if (!success) {
        console.warn("[Analytics] sendBeacon failed, falling back to fetch");
        await fetch(`${ANALYTICS_URL}/statistics`, {
          method: "POST",
          body: formData,
        });
      }
    } else {
      await fetch(`${ANALYTICS_URL}/statistics`, {
        method: "POST",
        body: formData,
      });
    }
  } catch (e) {
    console.warn("[Analytics] Failed to send player analytics:", e);
  }
}

export async function sendSessionAnalytics({
  visitorId = "unknown",
  userid = "unknown",
  ip,
  device = getDeviceType(),
  domain = getDomain(),
  region = getRegionByDomain(),
  pagelink = typeof location !== "undefined" ? location.href : "unknown",
  time = getCurrentTime(),
  screensize = typeof window !== "undefined" ? `${window.innerWidth} x ${window.innerHeight}` : "unknown",
  browser = "MSX",
  operationalsystem = "MSX",
  email = "unknown",
  country = "unknown",
  subscription = "not subscribed",
}: {
  visitorId?: string;
  userid?: string;
  ip?: string;
  device?: string;
  domain?: string;
  region?: string;
  pagelink?: string;
  time?: string;
  screensize?: string;
  browser?: string;
  operationalsystem?: string;
  email?: string;
  country?: string;
  subscription?: string;
} = {}): Promise<void> {
  // Получаем реальный IP, если не передан
  const realIP = ip || await getUserIP();
  
  const payload = {
    visitorId,
    userid,
    ip: realIP,
    device,
    domain,
    region,
    pagelink,
    time,
    screensize,
    browser,
    operationalsystem,
    email,
    country,
    subscription,
  };
  try {
    await fetch(`${ANALYTICS_URL}/firsttime/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {}
}

export async function sendSearchAnalytics({
  visitorId = "unknown",
  userid = "unknown",
  search_text = "",
  region = getRegionByDomain(),
  domain = getDomain(),
  currentTime = getCurrentTime(),
  country = "unknown",
  email = "unknown",
}: {
  visitorId?: string;
  userid?: string;
  search_text?: string;
  region?: string;
  domain?: string;
  currentTime?: string;
  country?: string;
  email?: string;
} = {}): Promise<void> {
  const payload = {
    visitorId,
    userid,
    search_text,
    region,
    domain,
    currentTime,
    country,
    email,
  };
  try {
    await fetch(`${ANALYTICS_URL}/searchinfo`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {}
}

export async function sendUtmAnalytics({
  utm_source = "unknown",
  utm_blogger = "unknown",
  utm_data = "{}",
  utm_url = "unknown",
  isRegisterVisited = false,
  temporaryId = "unknown",
  domain = getDomain(),
  region = getRegionByDomain(),
  country = "unknown",
  isVerified = "unknown",
  userId = null,
  userEmail = null,
  subscriptionApplyed = "unknown",
  subscribedFrom = "unknown",
  exitFrom = "unknown",
}: {
  utm_source?: string;
  utm_blogger?: string;
  utm_data?: string;
  utm_url?: string;
  isRegisterVisited?: boolean;
  temporaryId?: string;
  domain?: string;
  region?: string;
  country?: string;
  isVerified?: string;
  userId?: string | null;
  userEmail?: string | null;
  subscriptionApplyed?: string;
  subscribedFrom?: string;
  exitFrom?: string;
} = {}): Promise<void> {
  const payload = {
    utm_source,
    utm_blogger,
    utm_data,
    utm_url,
    isRegisterVisited,
    temporaryId,
    domain,
    region,
    country,
    isVerified,
    userId,
    userEmail,
    subscriptionApplyed,
    subscribedFrom,
    exitFrom,
  };
  try {
    await fetch(`${ANALYTICS_URL}/utm-statistics`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {}
}

export async function sendAdAnalytics({
  visitorId = "unknown",
  userid = "unknown",
  userRegion = getRegionByDomain(),
  domain = getDomain(),
  userCountry = "unknown",
  ads_id = "unknown",
  adStatus = "shown",
  playerType = "web",
}: {
  visitorId?: string;
  userid?: string;
  userRegion?: string;
  domain?: string;
  userCountry?: string;
  ads_id?: string;
  adStatus?: string;
  playerType?: string;
} = {}): Promise<void> {
  const payload = {
    visitorId,
    userid,
    userRegion,
    domain,
    userCountry,
    ads_id,
    adStatus,
    playerType,
  };
  try {
    await fetch(`${ANALYTICS_URL}/ads-stats/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
  } catch (e) {}
} 